/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {CSSProperties} from "react";
import {DialogTitle} from "../../../utils/DialogTitle";
import {PADDING_LEFT, PADDING_RIGHT} from "../../../utils/AppDialog";
import {VGap} from "../../widget/VGap";
import {PatientSearch} from "../../widget/PatientSearch";
import {HGap} from "../../widget/HGap";
import {DB_Patient} from "@app/app-shared/patient";
import {PatientSelectionButtons} from "./PatientSelectionButtons";
import {COLORS} from "@res/colors";
import {margin_l_lg} from "@styles/styles";
import {Filter} from "../../../utils/Filter";
import * as emotion from "emotion";
import {ICONS} from "@res/icons";
import {Checkbox} from "../patients/Checkbox";
import {Checkbox as WidgetCheckbox, CheckboxOptions, CheckboxOptionsType} from "../../widget/Checkbox";
import {MetadataModule} from "@modules/MetadataModule";
import {getUserOrgRoles, hasPermissionOrgRoles} from "@modules/utils";
import {ID_ALL, messagingRoles} from "@app/app-shared/permission";
import {OrganizationsContext, OrganizationsContextType} from "../organizations/OrganizationsContext";


type Props = {
    patients: DB_Patient[]
    selectedPatients: string[]
    onSelectionChange: (selectedPatientIds: string[]) => void
    onCancel: () => void
    onOK: (selectedPatients: string[]) => void,
    title?: string,
    okText: string,
    loading?: boolean
}

type State = {
    all: boolean,
    searchFilter?: string
    organizationsFilter: CheckboxOptions[],
    organizationsMap: Record<string, DB_Patient[]>,
    organizationList: CheckboxOptions[],
    subGroupsMap: Record<string, DB_Patient[]>
    subGroupFilter: CheckboxOptions[]
    patientsFilter: DB_Patient[]
    selectedPatients: string[]
}


export class PatientSelectionPanel
    extends React.Component<Props, State> {

    constructor(props: Props) {
        super(props);

        this.state = {
            all: this.props.patients ? this.props.patients.length > 0 && this.props.selectedPatients.length === this.props.patients?.length : false,
            organizationsFilter: [],
            organizationsMap: this.getOrganizationsToPatientsMap(this.props.patients),
            organizationList: [],
            subGroupFilter: [],
            subGroupsMap: {},
            patientsFilter: [],
            selectedPatients: []
        }
    }

    componentDidMount() {
        const patients = this.props.patients
        if (!patients)
            return
        MetadataModule.getSubGroups(patients)
            .then(subGroupsMap => this.setState({subGroupsMap}))
            .catch(e => console.error("Failed to retrieve subgroups and user groups", e))
    }

    private renderLoader = () => {
        return <div className={'absolute top left fill'} style={{...!this.props.loading && {display: 'none'}, zIndex: 9999}}>
            <div className={'absolute top left fill'} style={{backgroundColor: COLORS.neutral_gray_2, opacity: 0.2}}/>
            <div className={'absolute top left right bottom center_h center_v'} style={{width: 'fit-content', height: 'fit-content'}}>
                <div className={`loader`} style={{margin: 0, transform: 'translate(-50%, -50%)'}}/>
            </div>
        </div>;
    };

    render() {
        const selectedPatients = this.props.selectedPatients;

        let patients = this.props.patients;
        const {searchFilter, subGroupFilter, organizationsFilter, organizationsMap, subGroupsMap} = this.state


        if (subGroupFilter.length > 0 || organizationsFilter.length > 0)
            patients = this.state.patientsFilter

        if (searchFilter && patients)
            patients = patients.filter(p => `${p.name} - ${p.unitId}`.includes(searchFilter))

        return <OrganizationsContext.Consumer>
            {orgCtx => <div className={'ll_v_s fill'} style={{width: 884, borderRadius: 16, background: 'white'}}>
                {this.renderLoader()}
                <div style={{display: "flex", justifyContent: "space-between", marginRight: 20}}>
                    <DialogTitle title={this.props.title || `Compose new message - Choose from list`}/>
                </div>
                <div className={'ll_v_s'} style={{flex: 1, paddingLeft: PADDING_LEFT, paddingRight: PADDING_RIGHT}}>
                    <VGap v={25}/>
                    <div className={'ll_h_c'} style={{marginRight: 10}}>
                        <div className={`ll_h_c ${margin_l_lg}`}>
                            {this.getOrganizationsCheckBox(orgCtx, organizationsMap, CheckboxOptionsType.Organizations, organizationsFilter, "Organizations")}
                            {Object.keys(subGroupsMap).length > 1 && this.getSubGroupsCheckBox(subGroupsMap, CheckboxOptionsType.SubGroups, subGroupFilter, "Sub User Groups")}
                            <PatientSearch
                                patients={patients}
                                selectedPatients={selectedPatients}
                                onSelected={(selected) => {
                                    this.props.onSelectionChange(selected)
                                    this.setState({all: false, searchFilter: ""})
                                }}
                                onSearch={(text) => this.setState({searchFilter: text})}
                            />
                        </div>
                    </div>
                    <VGap v={20}/>
                    {this.renderPatients(patients || [])}
                    <div style={{flex: 1}}/>
                </div>
                <VGap v={47}/>
                <PatientSelectionButtons
                    patientCount={patients?.length}
                    selectedCount={selectedPatients.length}
                    okEnabled={true}
                    okText={this.props.okText}
                    cancelText={'Back'}
                    onCancel={this.props.onCancel}
                    onOK={() => this.props.onOK(selectedPatients)}
                />
            </div>}
        </OrganizationsContext.Consumer>;
    }

    private renderPatients = (patients: DB_Patient[]) => {
        const groups = this.groupPatients(patients);
        return <div className={'ll_h_s'} style={{maxHeight: 400, overflowY: 'auto'}}>
            {groups.map((chunk, idx) => this.renderPatientsColumn(chunk, idx))}
        </div>;
    };

    private groupPatients = (patients: DB_Patient[]): DB_Patient[][] => {
        if (!patients)
            return [];
        const groupCount = 3;
        const groups = [];
        const groupSize = Math.max(Math.ceil(patients.length / groupCount), 11);
        for (let i = 0; i < groupCount; i++) {
            const startIndex = i * groupSize;
            const endIndex = startIndex + groupSize;
            const column: DB_Patient[] = patients.slice(startIndex, endIndex);
            groups.push(column);
        }
        return groups
    };

    private renderPatientsColumn = (patients: DB_Patient[], idx: number) => {
        return <div key={idx} className={'ll_v_s'} style={{width: 300}}>
            {patients.map(this.renderPatient)}
        </div>;
    };

    private renderPatient = (patient: DB_Patient) => {
        const id = patient._id;
        const isSelected = this.props.selectedPatients.includes(id);
        return <div key={patient._id} className={'ll_h_c'} style={{minHeight: 34}}>
            <Checkbox on={isSelected} onChange={on => {
                if (on)
                    this.addToSelection(id);
                else
                    this.removeFromSelection(id);
            }}/>
            <HGap h={5}/>
            <div style={{fontSize: 16, color: "#303030"}}>{patient.name} - {patient.unitId}</div>
        </div>;
    };

    private addToSelection = (id: string) => {
        const selectedPatients = this.props.selectedPatients;
        selectedPatients.push(id);
        this.props.onSelectionChange(selectedPatients);
        this.setState({all: false})
    };

    private removeFromSelection = (id: string) => {
        const selectedPatients = this.props.selectedPatients.filter(it => it !== id);
        this.props.onSelectionChange(selectedPatients);
        this.setState({all: false})
    };

    private getOrganizationsCheckBox = (orgCtx: OrganizationsContextType, mapToPatients: Record<string, DB_Patient[]>, type: CheckboxOptionsType, checkboxFilter: CheckboxOptions[], label: string) => {
        const organizationsPermissions = getUserOrgRoles()
        const optionsList: CheckboxOptions[] = [];
        if (Object.keys(organizationsPermissions).includes(ID_ALL) && organizationsPermissions[ID_ALL].some(r => messagingRoles.includes(r))) {
            optionsList.push(...Object.values(orgCtx.organizations).map(org => ({name: org.name, _id: org._id, source: "", count: 0, type})));
        } else {
            Object.keys(organizationsPermissions).reduce((carry: CheckboxOptions[], _id) => {
                if (_id === ID_ALL && organizationsPermissions[ID_ALL].some(r => messagingRoles.includes(r))) {
                    return carry;
                }
                const org = orgCtx.organizations[_id];
                if (!org)
                    return carry;

                if (hasPermissionOrgRoles(organizationsPermissions, org._id, messagingRoles))
                    carry.push({name: org.name, _id, source: "", count: 0, type})

                return carry;
            }, optionsList)
        }

        optionsList.unshift({name: label, _id: "", source: "", count: 0, type: type})

        let updateCheckboxFilter = checkboxFilter

        return <WidgetCheckbox
            isChecked={(option: CheckboxOptions) => updateCheckboxFilter.some(filter => filter.name === option.name)}
            options={optionsList}
            onClick={(filter: string) => {
                //press first option
                if (filter === label) {
                    const patientsToRemovePatients = this.getAllSelectedPatients(updateCheckboxFilter, type, mapToPatients);
                    const _patientsIds = patientsToRemovePatients.map(p => p._id)
                    this.setAndSelectFilterState([], type, this.state.patientsFilter.filter(p => !patientsToRemovePatients.includes(p)), this.state.selectedPatients.filter(id => !_patientsIds.includes(id)));
                    updateCheckboxFilter = [];
                    return;
                }
                let addOrRemovePatients: DB_Patient[] = [];

                const organization = optionsList.find(o => o.name === filter);
                const organizationId = organization?._id;
                if (organizationId)
                    addOrRemovePatients = mapToPatients[organizationId] ?? [];

                const patientsIds = addOrRemovePatients.map(p => p._id)
                const subGroupPatients = this.getAllSelectedPatients(this.state.subGroupFilter, CheckboxOptionsType.SubGroups, this.state.subGroupsMap)
                //remove patients
                if (updateCheckboxFilter.some(e => e.name === filter)) {
                    updateCheckboxFilter = updateCheckboxFilter.filter(o => o.name !== filter)
                    this.setAndSelectFilterState(updateCheckboxFilter.filter(o => o.name !== filter), type, this.state.patientsFilter.filter(p => !addOrRemovePatients.includes(p)), this.state.selectedPatients.filter(id => !patientsIds.includes(id)))
                }
                //add patients
                else {
                    const commonElements = subGroupPatients.length > 0 ? [...this.state.patientsFilter, ...addOrRemovePatients].filter(p => subGroupPatients.includes(p)) : [...this.state.patientsFilter, ...addOrRemovePatients];
                    const commonElementsIds = commonElements.map(p => p._id)
                    const addedFilter = {name: filter, _id: organizationId, source: "", count: 0, type: type}
                    this.setAndSelectFilterState([...updateCheckboxFilter, addedFilter], type, commonElements, commonElementsIds)
                    updateCheckboxFilter = [...updateCheckboxFilter, addedFilter]
                }
            }
            }
            iconOpen={<Filter inline={true} filterList={updateCheckboxFilter} filterType={type} filterFiller={label}
                              open={false}/>}
            iconClose={<Filter inline={true} filterList={updateCheckboxFilter} filterType={type} filterFiller={label}
                               open={true}/>}
            checkboxRender={this.renderCheck}
            rtl={true}
            outerNode={() => emotion.css`margin-left: auto`}
            containerClass={() => emotion.css`width: 100%`}
            renderLabel={this.renderLabel}
        />
    }

    private getSubGroupsCheckBox = (mapToPatients: Record<string, DB_Patient[]>, type: CheckboxOptionsType, checkboxFilter: CheckboxOptions[], label: string) => {
        const optionsList = this.getCheckBoxList(mapToPatients, type)
        const organizationGroupPatients = this.getAllSelectedPatients(this.state.organizationsFilter, CheckboxOptionsType.Organizations, this.state.organizationsMap)
        optionsList.unshift({name: label, _id: "", source: "", count: 0, type: type})
        let updateCheckboxFilter = checkboxFilter

        return <WidgetCheckbox
            isChecked={(option: CheckboxOptions) => {
                return updateCheckboxFilter.some(filter => filter.name === option.name)
            }}
            options={optionsList}
            onClick={(filter: string) => {
                const subGroupsPatients = this.getAllSelectedPatients(updateCheckboxFilter, type, mapToPatients);
                //press first option
                if (filter === label) {
                    const patientsIds = organizationGroupPatients.map(p => p._id)
                    this.setAndSelectFilterState([], type, organizationGroupPatients, patientsIds);
                    updateCheckboxFilter = [];
                    return;
                }

                const addOrRemovePatients = mapToPatients[filter] ?? [];

                //remove patients
                if (updateCheckboxFilter.some(e => e.name === filter)) {
                    const removeSubUserPatients = subGroupsPatients.filter(p => !addOrRemovePatients.includes(p))
                    const commonElements = removeSubUserPatients.length === 0 ? organizationGroupPatients : organizationGroupPatients.filter(element => removeSubUserPatients.includes(element));
                    const commonElementsIds = commonElements.map(p => p._id)
                    this.setAndSelectFilterState(updateCheckboxFilter.filter(o => o.name !== filter), type, commonElements, commonElementsIds)
                    updateCheckboxFilter = updateCheckboxFilter.filter(o => o.name !== filter)
                }
                //add patients
                else {
                    const addedSubUserPatients = [...subGroupsPatients, ...addOrRemovePatients]
                    const commonElements = addedSubUserPatients.filter(element => organizationGroupPatients.includes(element));
                    const commonElementsIds = commonElements.map(p => p._id)
                    const addedFilter = {name: filter, _id: "", source: "", count: 0, type: type}
                    this.setAndSelectFilterState([...updateCheckboxFilter, addedFilter], type, commonElements, commonElementsIds)
                    updateCheckboxFilter = [...updateCheckboxFilter, addedFilter]
                }
            }
            }
            iconOpen={<Filter inline={true} filterList={updateCheckboxFilter} filterType={type} filterFiller={label}
                              open={false}/>}
            iconClose={<Filter inline={true} filterList={updateCheckboxFilter} filterType={type} filterFiller={label}
                               open={true}/>}
            checkboxRender={this.renderCheck}
            rtl={true}
            outerNode={(checked: boolean, disabled: boolean) => emotion.css`margin-left: auto`}
            containerClass={(checked: boolean, disabled: boolean) => emotion.css`width: 100%`}
            renderLabel={this.renderLabel}
        />

    }


    private renderCheck = (checked: boolean, disabled: boolean) => {
        if (!checked)
            return null;
        return <div>{ICONS.icon_tick_blue(COLORS.primary_blue, 16)}</div>;
    };

    private renderLabel = (checked: boolean, disabled: boolean) => {
        const css: CSSProperties = {
            fontWeight: checked ? "bold" : "normal",
            color: COLORS.neutral_text_dark
        };
        return css;
    };

    private getOrganizationsToPatientsMap(patients: DB_Patient[] | undefined): Record<string, DB_Patient[]> {
        if (!patients)
            return {}

        const organizationsMap: Record<string, DB_Patient[]> = {};

        patients.forEach(patient => {
            const organizationId = patient.organizationId;
            if (organizationId in organizationsMap) {
                organizationsMap[organizationId].push(patient);
            } else {
                organizationsMap[organizationId] = [patient];
            }
        });
        return organizationsMap;
    }


    private getCheckBoxList(Map: Record<string, DB_Patient[]>, type: CheckboxOptionsType): CheckboxOptions[] {
        return Object.keys(Map).filter(key => key.trim() !== "").map(item => ({name: item, _id: "", source: "", count: 0, type}));
    }

    private setAndSelectFilterState(filters: CheckboxOptions[], type: CheckboxOptionsType, patientsFilter: DB_Patient[], selectedPatients: string[]) {
        this.props.onSelectionChange(selectedPatients)
        const stateKeyMap: Record<string, string> = {
            [CheckboxOptionsType.SubGroups]: 'subGroupFilter',
            [CheckboxOptionsType.Organizations]: 'organizationsFilter'
        };

        const stateKey = stateKeyMap[type];
        this.setState((prevState) => ({
            ...prevState,
            [stateKey]: filters,
            patientsFilter,
            selectedPatients
        }));
    }

    private getAllSelectedPatients(updateCheckboxFilter: any[], type: CheckboxOptionsType, mapToPatients: Record<string, DB_Patient[]>): DB_Patient[] {
        return updateCheckboxFilter
            .map(key => {
                const idOrName = type === CheckboxOptionsType.Organizations ? key?._id : key?.name;
                if (!key || !idOrName) {
                    return [];
                }
                return mapToPatients[idOrName] || [];
            })
            .reduce((acc, val) => acc.concat(val), []);
    }


}

